import { Select, Table, type TableProps } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AssignButton } from '@client/components/buttons/AssignButton';
import { HideColumns } from '@client/components/table/HideColumns';
import { Toolbar } from '@client/components/Toolbar';
import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAssignHandlers } from '@client/hooks/useAssignHandlers';
import { useAuth } from '@client/hooks/useAuth';
import { useColumnsToggle } from '@client/hooks/useColumnsToggle';
import { useTable } from '@client/hooks/useTable';
import { columnsMetadata } from '@client/pages/features/columns.metadata';
import styles from '@client/pages/features/features.module.less';
import { useFeatureQueries } from '@client/queries/useFeatureQueries';
import { assignStore, type TAssignedState } from '@client/store/assigned.store';
import { useDirty } from '@client/store/dirty.store';
import { useFeature } from '@client/store/feature.store';
import { useLoading } from '@client/store/loading.store';
import { useLocale } from '@client/store/locale.store';
import { indexable } from '@client/utils/table/table.util';
import { EFeatureType, type IFeature } from '@common/types';

import type { TColumns } from '@client/types/antd.type';
import type { User } from 'firebase/auth';

export const FeaturesDrawerContent: React.FC = () => {
  const { user, ability } = useAuth();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { setDirty } = useDirty();
  const { featureType, setFeatureType } = useFeature();
  const { useActiveFeatures } = useFeatureQueries();

  const { data, refetch, isLoading, isFetching } =
    useActiveFeatures(featureType);

  const features = data?.features ?? [];
  const total = data?.total ?? 0;

  const { loading } = useLoading(isLoading || isFetching);

  const { onAssign, isAssigned } = useAssignHandlers<
    IFeature,
    TAssignedState<IFeature>
  >(features, assignStore);

  const createFeatureTypeOptions = () =>
    (Object.keys(EFeatureType) as (keyof typeof EFeatureType)[])
      ?.sort()
      .map((type, idx) => ({
        key: idx,
        label: EFeatureType[type],
        value: type,
      }));

  const {
    tableParams: { pagination },
    filteredInfo,
    sortedInfo,
    handleTableChange,
  } = useTable<IFeature>(features, total, { pageSize: 9, syncUrl: false });

  const disabled = ability.cannot(
    EAbilityActions.ASSIGN,
    `${EAbilitySubjects.SUBSCRIPTION}.${EAbilitySubjects.FEATURES}`,
  );

  const columns: TableProps<IFeature>['columns'] = [
    ...columnsMetadata(
      {
        locale,
        filteredInfo,
        sortedInfo,
        entities: features,
      },
      ['title', 'status'],
    ),
    {
      title: t('table.actions'),
      align: 'center',
      fixed: 'right',
      width: 150,
      render(record: IFeature) {
        return (
          <AssignButton
            loading={loading}
            disabled={disabled}
            behavior={{
              isAssigned: () => isAssigned(record),
              confirm: t('subscription.msg.unAssignConfirm'),
            }}
            onClick={() => {
              onAssign(record);
              setDirty(true);
            }}
          />
        );
      },
    },
  ];

  const { filteredColumns, columnsList, selectedColumns, setSelectedColumns } =
    useColumnsToggle(columns as TColumns[]);

  const tableProps = {
    columns: filteredColumns as TColumns<IFeature>[],
    pagination,
    scroll: { x: 650 },
    bordered: true,
    className: styles.gridList,
    dataSource: indexable<IFeature>(features),
    loading,
    rowKey: (record: IFeature) => record.id ?? `row-${Math.random()}`,
    onChange: handleTableChange,
    title: () => (
      <div className={styles.gridHeader}>
        <Toolbar<IFeature[]>
          entities={features}
          onRefresh={refetch}
          subject={`${EAbilitySubjects.FEATURES}.${user?.uid as User['uid']}`}
        >
          <HideColumns
            columnsList={columnsList}
            selectedColumns={selectedColumns}
            onChange={setSelectedColumns}
          />
        </Toolbar>
      </div>
    ),
    footer: () => (
      <div className={styles.gridFooter}>
        {t('table.total', { amount: features.length })}
      </div>
    ),
  };

  return (
    <>
      <Select
        style={{ width: 200, marginBottom: 16 }}
        aria-label="feature-type"
        value={featureType}
        defaultValue={featureType}
        loading={loading}
        disabled={disabled}
        onChange={setFeatureType}
        options={createFeatureTypeOptions()}
      />
      <Table<IFeature> {...tableProps} />
    </>
  );
};
